"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "../../lib/utils"
import { useUserPreferences } from "../../shared/context"
import { useDirectionSafe } from "../../shared/contexts/DirectionContext"
import { getDateFnsLocale } from "../../shared/utils/dateFnsLocale"
import { buttonVariants } from "./button"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

// Static Tailwind class names for react-day-picker slots. Hoisted to module
// scope (not user-facing copy) so the jsx-only i18n lint rule does not flag them.
const DAY_PICKER_CLASS_NAMES: CalendarProps["classNames"] = {
  months: "flex flex-col sm:flex-row gap-2 relative",
  month: "flex flex-col gap-4",
  month_caption: "flex justify-center pt-1 relative items-center w-full",
  caption_label: "text-sm font-medium",
  nav: "absolute top-1 left-0 right-0 flex items-center justify-between px-1 pointer-events-none z-10",
  button_previous: cn(
    buttonVariants({ variant: "outline" }),
    "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 pointer-events-auto"
  ),
  button_next: cn(
    buttonVariants({ variant: "outline" }),
    "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 pointer-events-auto"
  ),
  month_grid: "w-full border-collapse",
  weekdays: "flex",
  weekday:
    "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem] text-center",
  week: "flex w-full mt-2",
  day: cn(
    "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
    "[&:has([aria-selected].outside)]:bg-accent/50",
  ),
  day_button: cn(
    buttonVariants({ variant: "ghost" }),
    "h-8 w-8 p-0 font-normal aria-selected:opacity-100"
  ),
  range_start: cn(
    "day-range-start !bg-primary/15 !rounded-l-md !rounded-r-none last:!rounded-r-md",
    "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground [&>button]:rounded-md",
  ),
  range_end: cn(
    "day-range-end !bg-primary/15 !rounded-r-md !rounded-l-none first:!rounded-l-md",
    "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground [&>button]:rounded-md",
  ),
  selected:
    "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md",
  today: "bg-accent text-accent-foreground rounded-md",
  outside:
    "outside text-muted-foreground opacity-50",
  disabled: "text-muted-foreground opacity-50",
  range_middle:
    "!bg-primary/15 !rounded-none first:!rounded-l-md last:!rounded-r-md [&>button]:!bg-transparent [&>button]:!text-foreground [&>button]:hover:!bg-primary/25 [&>button]:hover:!text-foreground",
  hidden: "invisible",
}

/**
 * The month names, weekday initials and week-start day come from the VIEWER, not
 * from react-day-picker's built-in default (English, Sunday-first) — an unlocalized
 * calendar shows "August / Su Mo Tu" to a Hebrew or Spanish user whose every other
 * date on the page is translated. The locale is resolved from the user's language
 * preference and the first column from their explicit week-start preference, so no
 * call site has to remember to pass either; both stay overridable by props for the
 * rare caller that renders a fixed-locale calendar.
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale,
  weekStartsOn,
  dir,
  ...props
}: CalendarProps) {
  const { preferredLanguage, weekStartsOn: preferredWeekStartsOn } = useUserPreferences()
  const { direction } = useDirectionSafe()

  return (
    <DayPicker
      locale={locale ?? getDateFnsLocale(preferredLanguage)}
      weekStartsOn={weekStartsOn ?? preferredWeekStartsOn}
      dir={dir ?? direction}
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        ...DAY_PICKER_CLASS_NAMES,
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight
          // `orientation` is NOT direction-aware on this render path. Only
          // react-day-picker's `navLayout="around"` branch resolves it from
          // `dir`; the default `Nav` component we use hardcodes "left" for
          // Previous and "right" for Next. So RTL mirrored the nav's LAYOUT via
          // `dir` while the glyphs stayed put, leaving both month arrows
          // pointing inward at each other in Hebrew. We mirror them ourselves.
          // If this ever moves to `navLayout="around"`, drop this class — that
          // path already swaps, and the two together would cancel out.
          return <Icon className="h-4 w-4 rtl:rotate-180" />
        },
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
